// src/pages/SignUp/signup.tsx

import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { signUp, resetSignUpState } from "../../store/signup_slice";
import Modal from "../../common/modal/modal";
import { AppDispatch, RootState } from "../../store/store";
import Button from "../../common/button/button";
import FormInput from "../../common/form_input/form_input";
import TermsModal from "./terms_modal";

interface SignUpPageProps {
  isOpen: boolean;
  onClose: () => void;
  openLoginModal: () => void;
}

function isPasswordValid(password: string) {
  // At least 8 characters, one uppercase, one number, one special char
  if (!password) return false;
  const minLength = password.length >= 8;
  const hasUpper = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);
  return minLength && hasUpper && hasNumber && hasSpecial;
}

const SignUpPage: React.FC<SignUpPageProps> = ({
  isOpen,
  onClose,
  openLoginModal,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, success } = useSelector(
    (state: RootState) => state.signup
  );

  const [isTermsModalOpen, setIsTermsModalOpen] = React.useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = React.useState(false);

  // Validation schema
  const validationSchema = Yup.object({
    firstName: Yup.string()
      .max(50, "First name cannot exceed 50 characters")
      .required("First name is required"),
    lastName: Yup.string()
      .max(50, "Last name cannot exceed 50 characters")
      .required("Last name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    phoneNumber: Yup.string()
      .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
      .required("Phone number is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), ""], "Passwords must match")
      .required("Confirm your password"),
    acceptTerms: Yup.boolean()
      .oneOf([true], "You must accept the terms and conditions")
      .required("You must accept the terms and conditions"),
  });

  // Initialize formik
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
      acceptTerms: false,
    },
    validationSchema,
    onSubmit: async (values): Promise<boolean> => { // Specificăm explicit return type
      try {
        const result = await dispatch(signUp({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phoneNumber: values.phoneNumber,
          password: values.password,
        }));

        if (!('error' in result)) {
          formik.resetForm();
          return true;
        }
        return false;
      } catch (error) {
        console.error('Signup submission error:', error);
        return false;
      }
    },
  });

  // Handle successful registration
  React.useEffect((): (() => void) | undefined => {
    if (success) {
      const timer = setTimeout(() => {
        dispatch(resetSignUpState());
        onClose();
        openLoginModal();
      }, 1500);

      return () => clearTimeout(timer);
    }
    return undefined; // Explicit return for when success is false
  }, [success, onClose, openLoginModal, dispatch]);

  // Handle modal close
  const handleClose = () => {
    formik.resetForm();
    dispatch(resetSignUpState());
    onClose();
  };

  // Switch to login
  const handleLoginClick = () => {
    handleClose();
    openLoginModal();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose}>
        <div className="w-full max-w-md mx-auto">
          {/* Header */}
          <h2 className="text-base font-ubuntu font-medium mb-2">
            Sign Up
          </h2>
          <div className="border-b-2" />
          <div className="text-center my-6 font-ubuntu font-medium text-primary">
            Create your account
          </div>

          {/* Error Display */}
          {error && (
            <div className="text-red-500 text-sm mb-4 text-center">{error}</div>
          )}

          {/* Signup Form */}
          <form onSubmit={formik.handleSubmit} className="px-6">
            <FormInput
              label="First Name"
              type="text"
              name="firstName"
              value={formik.values.firstName}
              placeholder="Enter your first name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={formik.touched.firstName ?? false}
              error={formik.errors.firstName}
              disabled={loading}
            />

            <FormInput
              label="Last Name"
              type="text"
              name="lastName"
              value={formik.values.lastName}
              placeholder="Enter your last name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={formik.touched.lastName ?? false}
              error={formik.errors.lastName}
              disabled={loading}
            />

            <FormInput
              label="Email"
              type="email"
              name="email"
              value={formik.values.email}
              placeholder="Enter your email"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={formik.touched.email ?? false}
              error={formik.errors.email}
              disabled={loading}
            />

            <FormInput
              label="Phone Number"
              type="tel"
              name="phoneNumber"
              value={formik.values.phoneNumber}
              placeholder="Enter your phone number"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={formik.touched.phoneNumber ?? false}
              error={formik.errors.phoneNumber}
              disabled={loading}
            />

            <FormInput
              label="Password"
              type="password"
              name="password"
              value={formik.values.password}
              placeholder="Enter your password"
              onChange={formik.handleChange}
              onFocus={() => setIsPasswordFocused(true)}
              onBlur={e => {
                setIsPasswordFocused(false);
                formik.handleBlur(e);
              }}
              touched={formik.touched.password ?? false}
              error={formik.errors.password}
              disabled={loading}
            />

            {/* Password requirements info message, shown on password input focus and if not valid */}
            {isPasswordFocused && !isPasswordValid(formik.values.password) && (
              <div className="text-xs text-gray-600 mt-1 mb-2">
                Password must be at least 8 characters long and include at least one uppercase letter, one number, and one special character.
              </div>
            )}
            <FormInput
              label="Confirm Password"
              type="password"
              name="confirmPassword"
              value={formik.values.confirmPassword}
              placeholder="Confirm your password"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={formik.touched.confirmPassword ?? false}
              error={formik.errors.confirmPassword}
              disabled={loading}
            />

            {/* Terms and Conditions Checkbox */}
            <div className="mb-4">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  name="acceptTerms"
                  checked={formik.values.acceptTerms}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={loading}
                  className="mt-1 mr-2"
                />
                <label htmlFor="acceptTerms" className="text-sm text-gray-700">
                  I accept the{" "}
                  <button
                    type="button"
                    onClick={() => setIsTermsModalOpen(true)}
                    className="text-primary underline hover:text-indigo-800"
                  >
                    Terms & Conditions
                  </button>
                </label>
              </div>
              {formik.touched.acceptTerms && formik.errors.acceptTerms && (
                <div className="text-red-500 text-sm mt-1">{formik.errors.acceptTerms}</div>
              )}
            </div>

            <Button
              type="submit"
              buttonType="primary"
              className="w-full bg-button font-ubuntu text-xl text-white p-3 rounded-lg hover:bg-indigo-800 transition duration-200 ease-in-out"
              disabled={loading}
            >
              {loading
                ? "Signing up..."
                : "Sign Up"}
            </Button>
          </form>

          {/* Login Link */}
          <div className="px-6 my-6 font-ubuntu text-primary text-xs text-center">
            Already have an account?{" "}
            <span
              className="underline font-medium text-base cursor-pointer hover:text-indigo-800 transition-colors duration-200"
              onClick={handleLoginClick}
            >
              Login
            </span>
          </div>
        </div>
      </Modal>

      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />
    </>
  );
};

export default SignUpPage;
